/**
 * Vidya — AI chatbot for parents.
 *
 * Composition:
 *   - useChatHistory  : loads + maintains the chat history; exposes send/reset
 *   - ChatHeader      : the top bar with the avatar + reset button
 *   - SuggestionGrid  : starter prompts when there's no history
 *   - MessageBubble   : single rendered user/assistant message
 *   - ChatComposer    : textarea + send button
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatCircleDots, PaperPlaneRight, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How is my child doing in academics?",
  "What are my pending fees?",
  "Show me the latest marksheet results",
  "Any progress reports this term?",
];

function useChatHistory() {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/parent/chat/history")
      .then((r) => setMessages((r.data || []).filter((m) => m.role !== "system_error")))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    setBusy(true);
    const nowIso = new Date().toISOString();
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: trimmed, created_at: nowIso }]);
    try {
      const res = await api.post("/parent/chat", { message: trimmed });
      setMessages((m) => [
        ...m.filter((x) => !String(x.id).startsWith("tmp-")),
        res.data.user_message,
        res.data.assistant_message,
      ]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
    } finally {
      setBusy(false);
    }
  }, []);

  const reset = useCallback(async () => {
    if (!window.confirm("Clear all chat history?")) return;
    try {
      await api.post("/parent/chat/reset");
      setMessages([]);
      toast.success("Chat reset");
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  return { messages, busy, send, reset };
}

function ChatHeader({ onReset }) {
  return (
    <div className="px-5 py-3 border-b border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center">
          <Sparkle size={18} weight="duotone" />
        </div>
        <div>
          <div className="font-display text-sm font-semibold">Vidya · AI assistant</div>
          <div className="text-[10px] text-[var(--vs-text-secondary)]">Ask anything about your child's marks, fees or progress.</div>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onReset} data-testid="parent-chat-reset" title="Reset chat history">
        <ArrowsClockwise size={14} />
      </Button>
    </div>
  );
}

function SuggestionGrid({ onPick, busy }) {
  return (
    <div className="text-center py-8">
      <ChatCircleDots size={40} weight="duotone" className="mx-auto text-[var(--vs-secondary)] mb-2" />
      <div className="text-sm text-[var(--vs-text-secondary)] mb-4">Start by asking a question…</div>
      <div className="flex flex-wrap gap-2 justify-center">
        {SUGGESTIONS.map((s) => (
          <button
            key={s} onClick={() => onPick(s)} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full border border-[var(--vs-border)] bg-white hover:border-[var(--vs-secondary)] transition-colors"
            data-testid={`parent-chat-suggestion-${s.slice(0, 12)}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-[var(--vs-secondary)] text-[var(--vs-bg)]"
            : "bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]"
        }`}
        data-testid={`parent-chat-msg-${message.role}`}
      >
        {message.content}
      </div>
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="px-3 py-2 rounded-lg bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)] text-sm text-[var(--vs-text-secondary)]">
        Vidya is thinking…
      </div>
    </div>
  );
}

function ChatComposer({ onSend, busy }) {
  const [input, setInput] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSend(input);
    setInput("");
  };
  return (
    <form onSubmit={submit} className="border-t border-[var(--vs-border)] p-3 flex gap-2 items-end" data-testid="parent-chat-form">
      <Textarea
        data-testid="parent-chat-input"
        rows={1}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
        }}
        placeholder="Ask anything about your child…"
        className="bg-white resize-none"
      />
      <Button type="submit" disabled={busy || !input.trim()} data-testid="parent-chat-send" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
        <PaperPlaneRight size={14} />
      </Button>
    </form>
  );
}

export function ParentChat() {
  const { messages, busy, send, reset } = useChatHistory();
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  return (
    <div className="vs-card p-0 overflow-hidden flex flex-col h-[600px]" data-testid="parent-chat">
      <ChatHeader onReset={reset} />
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="parent-chat-stream">
        {messages.length === 0 && <SuggestionGrid onPick={send} busy={busy} />}
        {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
        {busy && <ThinkingIndicator />}
        <div ref={bottomRef} />
      </div>
      <ChatComposer onSend={send} busy={busy} />
    </div>
  );
}
